import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import { subscriptionAPI } from '../api';
import { Gift, Infinity } from 'lucide-react';

/**
 * CompactUsageCounter - A small pill showing usage count in the header
 * 
 * Features:
 * - Color dot: green (>50% remaining), amber (20-50%), red (<20%)
 * - Gift icon + amber theme for code users
 * - ∞ symbol for founder users
 * - Crash-safe with optional chaining on all fields
 * - Refreshes on 'usage-updated' custom event
 */
const CompactUsageCounter = () => {
  const { user } = useContext(UserContext);
  const [usageInfo, setUsageInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch usage info
  const fetchUsage = async () => {
    if (!user?.user_id) {
      setLoading(false);
      return;
    }

    try {
      const response = await subscriptionAPI.getUsageInfo(user.user_id);
      setUsageInfo(response.data);
    } catch (error) {
      console.warn('[CompactUsageCounter] Failed to fetch usage:', error);
      setUsageInfo(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchUsage();
  }, [user?.user_id]);

  // Listen for usage-updated events (fired after analysis)
  useEffect(() => {
    const handleUsageUpdate = () => {
      fetchUsage();
    };

    window.addEventListener('usage-updated', handleUsageUpdate);
    return () => window.removeEventListener('usage-updated', handleUsageUpdate);
  }, [user?.user_id]);

  // CRASH SAFETY: Return null if no usageInfo
  if (loading || !usageInfo) {
    return null;
  }

  // Extract fields with optional chaining
  const used = usageInfo?.analyses_used ?? 0;
  const limit = usageInfo?.analyses_limit ?? 0;
  const remaining = usageInfo?.analyses_remaining ?? 0;
  const period = usageInfo?.period;
  const isCodeActive = usageInfo?.is_code_active === true;
  const isFounder = period === 'unlimited';

  // Calculate percentage remaining for color dot
  const percentRemaining = limit > 0 ? (remaining / limit) * 100 : 100;

  // Determine dot color
  const getDotColor = () => {
    if (isFounder) return 'bg-purple-500'; // Founder gets purple
    if (isCodeActive) return 'bg-amber-500'; // Code users get amber
    if (percentRemaining > 50) return 'bg-emerald-500';
    if (percentRemaining > 20) return 'bg-amber-500';
    return 'bg-red-500';
  };

  // Determine pill background
  const getPillBg = () => {
    if (isCodeActive) return 'bg-amber-50 border-amber-200';
    if (isFounder) return 'bg-purple-50 border-purple-200';
    return 'bg-slate-50 border-slate-200';
  };

  // Determine text color
  const getTextColor = () => {
    if (isCodeActive) return 'text-amber-800';
    if (isFounder) return 'text-purple-800';
    return 'text-slate-700';
  };

  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${getPillBg()} ${getTextColor()}`}
      data-testid="usage-counter-pill"
      title={usageInfo?.display_text || `${used} / ${limit} analyses`}
    >
      {/* Status dot */}
      <span 
        className={`w-2 h-2 rounded-full ${getDotColor()}`}
        data-testid="usage-status-dot"
      />
      
      {/* Icon for code users */}
      {isCodeActive && (
        <Gift className="w-3 h-3 text-amber-600" data-testid="usage-code-icon" />
      )}
      
      {/* Counter display */}
      {isFounder ? (
        <span className="flex items-center gap-0.5" data-testid="usage-founder-display">
          <Infinity className="w-3.5 h-3.5" />
        </span>
      ) : (
        <span data-testid="usage-count-display">
          {used}/{limit}
        </span>
      )}
    </div>
  );
};

export default CompactUsageCounter;
